// ============================================================
//  src/pages/bots/automations/compiler.js
//
//  Converts a React Flow canvas (nodes + edges) into a flat
//  ActionRunner-compatible compiled actions array.
//
//  Key rules:
//  • Trigger node is the entry point — its config becomes the
//    first context, not a runnable action.
//  • Each action node is compiled based on its subtype.
//  • Edges define execution order. Multiple outgoing edges from
//    named handles (list rows, buttons, if/else branches) are
//    compiled into if_else chains.
//  • ask_question emits a real ask_question action with
//    _automation_trigger injected so ActionRunner can resume.
//  • if_else and switch emit nested then/else action branches.
// ============================================================

/**
 * Main entry point.
 * @param {Array} nodes  - React Flow nodes array
 * @param {Array} edges  - React Flow edges array
 * @returns {Array}      - Compiled actions for ActionRunner
 */
export function compileAutomation(nodes, edges) {
  const nodeMap = {};
  nodes.forEach(n => { nodeMap[n.id] = n; });

  // Index edges by `${sourceId}::${handleId}`
  // React Flow stores null sourceHandle for un-labelled connections —
  // normalise to 'out' for trigger nodes, 'default' for action nodes.
  const edgeMap = {};
  edges.forEach(e => {
    // Determine the canonical handle id:
    // - explicit sourceHandle wins
    // - otherwise use the node's first outputHandle default
    const srcNode = nodeMap[e.source];
    const fallback = srcNode?.type === 'triggerNode' ? 'out' : 'default';
    const handle = e.sourceHandle || fallback;
    const key = `${e.source}::${handle}`;
    if (!edgeMap[key]) edgeMap[key] = [];
    edgeMap[key].push(e.target);
  });

  const triggerNode = nodes.find(n => n.type === 'triggerNode');
  if (!triggerNode) return [];

  const automationTrigger = triggerNode.data?.config?.keyword || '*';

  // Trigger node output handle is 'out' (see NodeShell line 143)
  const compiled = walkFrom(triggerNode.id, 'out', nodeMap, edgeMap, automationTrigger, new Set());

  // Safety: always return at least a placeholder so backend doesn't reject
  return compiled.length > 0 ? compiled : [{ type: '__empty__' }];
}

/**
 * Walk forward from a node's output handle, collecting compiled actions.
 * @param {string} sourceId       - Node id we're walking FROM
 * @param {string} handleId       - Which output handle ('out', row title, button id, etc.)
 * @param {Object} nodeMap
 * @param {Object} edgeMap
 * @param {string} automationTrigger - The keyword that starts this automation
 * @param {Set}    visited         - Cycle guard
 * @returns {Array}
 */
function walkFrom(sourceId, handleId, nodeMap, edgeMap, automationTrigger, visited) {
  const key = `${sourceId}::${handleId}`;
  const targets = edgeMap[key] || [];
  const actions = [];

  for (const targetId of targets) {
    if (visited.has(targetId)) continue; // cycle guard
    const nextVisited = new Set(visited);
    nextVisited.add(targetId);
    const compiled = compileNode(targetId, nodeMap, edgeMap, automationTrigger, nextVisited);
    actions.push(...compiled);
  }

  return actions;
}

/**
 * Compile a single node into one or more ActionRunner actions.
 */
function compileNode(nodeId, nodeMap, edgeMap, automationTrigger, visited) {
  const node = nodeMap[nodeId];
  if (!node) return [];

  const cfg = node.data?.config ?? {};
  const subtype = node.data?.subtype ?? node.data?.triggerType;

  // Action nodes use handle id 'default' for their single output.
  // Trigger nodes use 'out'. Condition nodes use named handles only.
  const isTrigger = node.type === 'triggerNode';
  const defaultHandle = isTrigger ? 'out' : 'default';

  // What comes after this node on its default output handle
  const downstream = () => walkFrom(nodeId, defaultHandle, nodeMap, edgeMap, automationTrigger, visited);

  switch (subtype) {

    // ── Trigger node (shouldn't be in the middle of a flow, but safe) ──
    case 'whatsapp_message':
    case 'whatsapp_button':
    case 'schedule':
    case 'webhook':
    case 'manual':
    case 'payment_received':
      return downstream();

    // ── Send Text ──────────────────────────────────────────────
    case 'send_text':
      return [
        { type: 'send_text', text: cfg.text ?? '' },
        ...downstream(),
      ];

    // ── Send Media ─────────────────────────────────────────────
    case 'send_media': {
      const mediaType = cfg.mediaType ?? 'image';
      const action = mediaType === 'image'
        ? { type: 'send_image', url: cfg.url ?? '', caption: cfg.caption ?? '' }
        : mediaType === 'document'
        ? { type: 'send_document', url: cfg.url ?? '', filename: cfg.filename ?? '', caption: cfg.caption ?? '' }
        : { type: 'send_image', url: cfg.url ?? '', caption: cfg.caption ?? '' }; // video/audio fallback
      return [action, ...downstream()];
    }

    // ── Send Buttons ───────────────────────────────────────────
    case 'send_buttons':
    case 'send_buttons_image': {
      const buttons = (cfg.buttons ?? []).filter(b => b.title);
      const action = {
        type: 'send_buttons',
        body: cfg.body ?? '',
        footer: cfg.footer ?? '',
        buttons: buttons.map(b => ({ id: b.id || slugify(b.title), title: b.title })),
        _automation_trigger: automationTrigger,
      };
      if (subtype === 'send_buttons_image' && cfg.image_url) {
        action.image_url = cfg.image_url;
        action.image_caption = cfg.image_caption ?? '';
      }

      // Each button's handle id = b.title (set by ActionNode outputHandles={btns.map(b => b.title)})
      // handle = title (matches the edge wiring / outputHandles in the UI)
      // id     = what WhatsApp actually sends back in button_reply.id —
      //          that's what {{message}} will hold on resume, so branch
      //          comparisons must use id, not the display title.
      const buttonBranches = buttons.map(b => ({
        handle: b.title,
        label: b.title,
        id: b.id || slugify(b.title),
      }));

      if (buttonBranches.length === 0) return [action, ...downstream()];

      // Same reasoning as send_list: the button tap is a separate future
      // message, so pause here and branch once the real reply arrives.
      const chain = buildIfElseChain(
        '{{message}}', '==',
        buttonBranches.map(b => ({
          value: b.id,
          then: walkFrom(nodeId, b.handle, nodeMap, edgeMap, automationTrigger, visited),
        })),
        downstream()
      );

      return [action, { type: 'await_reply', resume: chain }];
    }

    // ── Send CTA URL (single external-link button) ─────────────
    //  Distinct from send_buttons — WhatsApp's "cta_url" type only
    //  ever has ONE button and it always opens a URL. No branching
    //  by button choice here since there's only one possible tap.
    case 'send_cta_url':
      return [
        {
          type: 'send_cta_url',
          body: cfg.body ?? '',
          display_text: cfg.display_text ?? cfg.button_text ?? 'Open Link',
          url: cfg.url ?? '',
          header: cfg.header ?? '',
          footer: cfg.footer ?? '',
        },
        ...downstream(),
      ];

    // ── Send List ──────────────────────────────────────────────
    case 'send_list': {
      const sections = cfg.sections ?? [];
      const action = {
        type: 'send_list',
        body: cfg.body ?? '',
        button: cfg.buttonLabel ?? 'View Options',
        _automation_trigger: automationTrigger,
        sections: sections.map(s => ({
          title: s.title ?? '',
          rows: (s.rows ?? []).filter(r => r.title).map(r => ({
            id: r.id || slugify(r.title),
            title: r.title,
            description: r.description ?? '',
          })),
        })),
      };

      // Row handle ids = r.title (set by ActionNode outputHandles={rows.map(r => r.title)})
      const allRows = sections.flatMap(s => (s.rows ?? []).filter(r => r.title));

      if (allRows.length === 0) return [action, ...downstream()];

      // Check if an ask_question node is connected on the default handle
      const defaultTargets = edgeMap[`${nodeId}::default`] || [];
      const askNode = defaultTargets.map(id => nodeMap[id]).find(n => n?.data?.subtype === 'ask_question');

      if (askNode) {
        // ask_question handles the wait + resume; downstream handles branching
        return [action, ...downstream()];
      }

      // No ask_question: the row taps arrive as a SEPARATE incoming
      // message later (WhatsApp list_reply), never in this same run.
      // So we can't branch on {{message}} right now — it's still the
      // text that triggered this automation, not the customer's tap.
      // Pause here (await_reply) and only evaluate the branch once the
      // real reply comes in and re-enters with a fresh {{message}}.
      //
      // IMPORTANT: WhatsApp returns list_reply.id (not the row title) as
      // the reply text — that's what {{message}} holds on resume, so the
      // comparison value must be r.id (falling back to the same slugify
      // used when building the row itself), not r.title.
      const chain = buildIfElseChain(
        '{{message}}', '==',
        allRows.map(r => ({
          value: r.id || slugify(r.title),
          then: walkFrom(nodeId, r.title, nodeMap, edgeMap, automationTrigger, visited),
        })),
        downstream()
      );
      return [action, { type: 'await_reply', resume: chain }];
    }

    // ── Ask Question ───────────────────────────────────────────
    // This is the pause/wait node. It:
    //   1. Sends the question (optional)
    //   2. Saves user's next reply to storage.<save_as>
    //   3. Halts the current run (ActionStopException)
    //   4. On resume (next message), storage.<save_as> is already set,
    //      so the downstream if_else can check it immediately.
    case 'ask_question': {
      const saveAs = cfg.save_as || 'answer';
      const question = (cfg.question ?? '').trim();
      const actions = [];
      if (question) actions.push({ type: 'send_text', text: question });

      // Pause here. On the customer's NEXT message, ActionRunner saves it
      // to storage.<saveAs> and runs ONLY the downstream actions below —
      // it does not replay `question` or anything before this node.
      actions.push({
        type: 'await_reply',
        save_as: saveAs,
        resume: downstream(),
      });
      return actions;
    }

    // ── If / Else ──────────────────────────────────────────────
    case 'if_else': {
      const left  = cfg.left  ?? '';
      const op    = cfg.op    ?? '==';
      const right = cfg.right ?? '';

      // NOTE: ConditionNode (FlowBuilderPage.js) always emits handle ids
      // '✓ Yes' / '✗ No' for if_else nodes — these must match exactly, or
      // this node's branches silently compile to empty arrays. walkFrom()
      // always returns an array (never null/undefined), so a `||` fallback
      // here would never actually run since `[]` is truthy in JS; if a
      // second handle-naming convention is ever introduced, merge results
      // from both lookups instead of using `||`.
      const thenActions = walkFrom(nodeId, '✓ Yes', nodeMap, edgeMap, automationTrigger, visited);
      const elseActions = walkFrom(nodeId, '✗ No', nodeMap, edgeMap, automationTrigger, visited);

      return [{
        type: 'if_else',
        left,
        op,
        right,
        then: thenActions,
        else: elseActions,
      }];
    }

    // ── Switch ─────────────────────────────────────────────────
    case 'switch': {
      const left  = cfg.left ?? '';
      const cases = cfg.cases ?? [];

      // Build if_else chain: case1, case2, ..., default (downstream)
      const branches = cases.filter(c => c.value).map(c => ({
        value: c.value,
        then: walkFrom(nodeId, c.label || c.value, nodeMap, edgeMap, automationTrigger, visited),
      }));

      const defaultActions = walkFrom(nodeId, 'Default', nodeMap, edgeMap, automationTrigger, visited);
      const chain = buildIfElseChain(left, '==', branches, defaultActions);
      return chain;
    }

    // ── Set Variable ───────────────────────────────────────────
    case 'set_var':
      return [
        { type: 'set_storage', key: cfg.key ?? '', value: cfg.value ?? '' },
        ...downstream(),
      ];

    // ── Wait / Delay ───────────────────────────────────────────
    case 'wait':
      return [
        { type: 'wait', seconds: parseInt(cfg.seconds ?? 1) },
        ...downstream(),
      ];

    // ── End ────────────────────────────────────────────────────
    case 'end':
      return [{ type: 'conversation_clear' }];

    // ── Send Template ──────────────────────────────────────────
    case 'send_template':
      return [
        { type: 'send_template', name: cfg.name ?? '', language: cfg.language ?? 'en' },
        ...downstream(),
      ];

    // ── Send Flow (WhatsApp Flow) ──────────────────────────────
    case 'send_flow':
      return [
        {
          type: 'send_flow',
          flow_id: cfg.flow_id ?? '',
          header_text: cfg.header ?? '',
          body_text: cfg.body ?? '',
          flow_cta: cfg.button_label ?? 'Open',
        },
        ...downstream(),
      ];

    // ── HTTP POST ──────────────────────────────────────────────
    case 'http_post': {
      let body = {};
      let headers = {};
      try { body = JSON.parse(cfg.body_json || '{}'); } catch {}
      try { headers = JSON.parse(cfg.headers_json || '{}'); } catch {}
      return [
        {
          type: cfg.method === 'GET' ? 'http_get' : 'http_post',
          url: cfg.url ?? '',
          body,
          headers,
          save_as: cfg.save_as ?? 'api_result',
        },
        ...downstream(),
      ];
    }

    // ── Send Email ─────────────────────────────────────────────
    case 'send_email':
      return [
        {
          type: 'http_post',
          url: '{{panel.email_webhook_url}}',
          body: { to: cfg.to ?? '', subject: cfg.subject ?? '', body: cfg.body ?? '' },
          save_as: 'email_result',
        },
        ...downstream(),
      ];

    // ── Google Sheets ──────────────────────────────────────────
    case 'google_sheets':
      return [
        {
          type: 'http_post',
          url: '{{panel.sheets_webhook_url}}',
          body: { action: cfg.action ?? 'append', sheet_id: cfg.sheet_id ?? '', data: cfg.row ?? '' },
          save_as: 'sheets_result',
        },
        ...downstream(),
      ];

    // ── Loop ───────────────────────────────────────────────────
    case 'loop':
      return [
        {
          type: 'for_each',
          items: cfg.items ?? '',
          actions: downstream(),
        },
      ];

    // ── Request Payment (in-chat commerce) ──────────────────────
    // Two modes, matching ActionRunner::execRequestPayment() exactly:
    // - 'product' mode sends product_id, backend looks up name+price
    // - 'custom' mode sends description+amount directly
    case 'request_payment': {
      const action = {
        type: 'request_payment',
        body: cfg.body || 'Tap below to complete your payment:',
      };
      if (cfg.mode === 'custom') {
        action.description = cfg.description ?? '';
        action.amount = cfg.amount ?? '';
      } else {
        action.product_id = cfg.product_id ?? '';
      }
      if (cfg.button_text) action.button_text = cfg.button_text;
      return [action, ...downstream()];
    }

    default:
      // Unknown subtype — skip, continue downstream
      return downstream();
  }
}

// ── Helpers ────────────────────────────────────────────────────

/**
 * Build a nested if_else chain from a list of {value, then} branches.
 * The last else falls through to defaultActions.
 *
 * buildIfElseChain('{{message}}', '==', [
 *   { value: '500',   then: [...] },
 *   { value: '1000',  then: [...] },
 * ], [...defaultActions])
 *
 * →  { type:'if_else', left:'{{message}}', op:'==', right:'500',
 *       then: [...], else: { type:'if_else', ..., right:'1000', else: [...default] } }
 */
function buildIfElseChain(left, op, branches, defaultActions) {
  if (branches.length === 0) return defaultActions;

  const [first, ...rest] = branches;
  return [{
    type: 'if_else',
    left,
    op,
    right: first.value,
    then: first.then,
    else: buildIfElseChain(left, op, rest, defaultActions),
  }];
}

function slugify(str) {
  return (str ?? '').toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
}